import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const numOr = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newProduct, setNewProduct] = useState({
    sku: "",
    name: "",
    description: "",
    weight: 250,
    price: 0,
    stock: 0,
    low_stock_threshold: 10,
    is_active: true,
  });

  const [edits, setEdits] = useState({});
  const [editingProductId, setEditingProductId] = useState(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      let list = [];
      try {
        const res = await axios.get(`${API}/admin/products`, {
          withCredentials: true,
        });
        list = res.data || [];
      } catch {
        const res = await axios.get(`${API}/admin/dashboard`, {
          withCredentials: true,
        });
        list = res.data.products || [];
      }

      setProducts(list);

      const nextEdits = {};
      for (const p of list) {
        nextEdits[p.product_id] = {
          sku: p.sku ?? "",
          name: p.name ?? "",
          description: p.description ?? "",
          weight: numOr(p.weight, 250),
          price: numOr(p.price, 0),
          stock: numOr(p.stock, 0),
          low_stock_threshold: numOr(p.low_stock_threshold, 10),
          is_active: !!p.is_active,
        };
      }
      setEdits(nextEdits);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const onEditChange = (productId, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [key]: value,
      },
    }));
  };

  const startEdit = (product) => {
    setEditingProductId(product.product_id);
    setEdits((prev) => ({
      ...prev,
      [product.product_id]: {
        sku: product.sku ?? "",
        name: product.name ?? "",
        description: product.description ?? "",
        weight: numOr(product.weight, 250),
        price: numOr(product.price, 0),
        stock: numOr(product.stock, 0),
        low_stock_threshold: numOr(product.low_stock_threshold, 10),
        is_active: !!product.is_active,
      },
    }));
  };

  const cancelEdit = () => {
    setEditingProductId(null);
  };

  const createProduct = async () => {
    if (!newProduct.sku.trim() || !newProduct.name.trim()) {
      toast.error("SKU and Name are required");
      return;
    }

    if (numOr(newProduct.weight, 0) <= 0) {
      toast.error("Weight must be greater than 0");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/products`,
        {
          ...newProduct,
          weight: numOr(newProduct.weight, 250),
          price: numOr(newProduct.price, 0),
          stock: numOr(newProduct.stock, 0),
          low_stock_threshold: numOr(newProduct.low_stock_threshold, 10),
        },
        { withCredentials: true }
      );

      toast.success("Product created");

      setNewProduct({
        sku: "",
        name: "",
        description: "",
        weight: 250,
        price: 0,
        stock: 0,
        low_stock_threshold: 10,
        is_active: true,
      });

      await fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const saveProduct = async (productId) => {
    const payload = edits[productId];
    if (!payload) return;

    if (!payload.sku.trim() || !payload.name.trim()) {
      toast.error("SKU and Name are required");
      return;
    }

    if (numOr(payload.weight, 0) <= 0) {
      toast.error("Weight must be greater than 0");
      return;
    }

    try {
      await axios.put(
        `${API}/admin/products/${productId}`,
        {
          ...payload,
          weight: numOr(payload.weight, 250),
          price: numOr(payload.price, 0),
          stock: numOr(payload.stock, 0),
          low_stock_threshold: numOr(payload.low_stock_threshold, 10),
        },
        { withCredentials: true }
      );

      toast.success("Product updated");
      setEditingProductId(null);
      await fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (productId, currentValue) => {
    try {
      await axios.put(
        `${API}/admin/products/${productId}`,
        { is_active: !currentValue },
        { withCredentials: true }
      );
      toast.success(!currentValue ? "Product activated" : "Product deactivated");
      await fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed");
    }
  };

  const deleteProduct = async (productId, name) => {
    const ok = window.confirm(`Delete product "${name}"? This cannot be undone.`);
    if (!ok) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        withCredentials: true,
      });
      toast.success("Product deleted");
      if (editingProductId === productId) {
        setEditingProductId(null);
      }
      await fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const rows = useMemo(
    () =>
      products.map((p) => ({
        product: p,
        edit: edits[p.product_id],
      })),
    [products, edits]
  );

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Products</h1>
          <p className="text-muted-foreground mt-1">
            Add, edit, deactivate, or delete products
          </p>
        </div>

        {/* Add Product */}
        <div className="bg-card p-6 rounded-xl border border-white/5">
          <h2 className="text-lg font-semibold mb-4 text-foreground">
            Add New Product
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SKU *</Label>
              <Input
                value={newProduct.sku}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, sku: e.target.value })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, name: e.target.value })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Weight (grams)</Label>
              <Input
                type="number"
                value={newProduct.weight}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    weight: numOr(e.target.value, 250),
                  })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Price</Label>
              <Input
                type="number"
                value={newProduct.price}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    price: numOr(e.target.value, 0),
                  })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Stock</Label>
              <Input
                type="number"
                value={newProduct.stock}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    stock: numOr(e.target.value, 0),
                  })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Low Stock Threshold</Label>
              <Input
                type="number"
                value={newProduct.low_stock_threshold}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    low_stock_threshold: numOr(e.target.value, 10),
                  })
                }
                className="bg-background border-white/10"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Input
                value={newProduct.description}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    description: e.target.value,
                  })
                }
                className="bg-background border-white/10"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <Button onClick={createProduct} className="bg-primary hover:bg-primary/90">
              Add Product
            </Button>
            <Button
              variant="outline"
              className="border-white/10"
              onClick={() =>
                setNewProduct({
                  sku: "",
                  name: "",
                  description: "",
                  weight: 250,
                  price: 0,
                  stock: 0,
                  low_stock_threshold: 10,
                  is_active: true,
                })
              }
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Existing Products */}
        <div className="bg-card p-6 rounded-xl border border-white/5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              Existing Products
            </h2>
            <Button variant="outline" className="border-white/10" onClick={fetchProducts}>
              Refresh
            </Button>
          </div>

          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground">No products found</p>
          ) : (
            <div className="space-y-4">
              {rows.map(({ product: p, edit }) => {
                if (!edit) return null;

                const isEditing = editingProductId === p.product_id;

                return (
                  <div
                    key={p.product_id}
                    className="border border-white/5 rounded-xl p-4"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div>
                        <p className="font-semibold text-foreground text-lg">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          ID: <span className="font-mono">{p.product_id}</span>
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          SKU: {p.sku} | Price: ₹{p.price} | Stock: {p.stock}
                        </p>
                        <p className="text-xs mt-2">
                          Active status:{" "}
                          <span className={p.is_active ? "text-green-500" : "text-yellow-500"}>
                            {p.is_active ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {!isEditing ? (
                          <Button
                            variant="outline"
                            className="border-white/10"
                            onClick={() => startEdit(p)}
                          >
                            Edit
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={() => saveProduct(p.product_id)}
                              className="bg-primary hover:bg-primary/90"
                            >
                              Save
                            </Button>
                            <Button
                              variant="outline"
                              className="border-white/10"
                              onClick={cancelEdit}
                            >
                              Cancel
                            </Button>
                          </>
                        )}

                        <Button
                          variant="outline"
                          className="border-white/10"
                          onClick={() => toggleActive(p.product_id, p.is_active)}
                        >
                          {p.is_active ? "Deactivate" : "Activate"}
                        </Button>

                        <Button
                          variant="destructive"
                          onClick={() => deleteProduct(p.product_id, p.name)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="mt-6 pt-6 border-t border-white/5">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>SKU</Label>
                            <Input
                              value={edit.sku}
                              onChange={(e) =>
                                onEditChange(p.product_id, "sku", e.target.value)
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={edit.name}
                              onChange={(e) =>
                                onEditChange(p.product_id, "name", e.target.value)
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Weight (grams)</Label>
                            <Input
                              type="number"
                              value={edit.weight}
                              onChange={(e) =>
                                onEditChange(
                                  p.product_id,
                                  "weight",
                                  numOr(e.target.value, 250)
                                )
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Price</Label>
                            <Input
                              type="number"
                              value={edit.price}
                              onChange={(e) =>
                                onEditChange(
                                  p.product_id,
                                  "price",
                                  numOr(e.target.value, 0)
                                )
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Stock</Label>
                            <Input
                              type="number"
                              value={edit.stock}
                              onChange={(e) =>
                                onEditChange(
                                  p.product_id,
                                  "stock",
                                  numOr(e.target.value, 0)
                                )
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Low Stock Threshold</Label>
                            <Input
                              type="number"
                              value={edit.low_stock_threshold}
                              onChange={(e) =>
                                onEditChange(
                                  p.product_id,
                                  "low_stock_threshold",
                                  numOr(e.target.value, 10)
                                )
                              }
                              className="bg-background border-white/10"
                            />
                          </div>

                          <div className="space-y-2 md:col-span-2 lg:col-span-3">
                            <Label>Description</Label>
                            <Input
                              value={edit.description}
                              onChange={(e) =>
                                onEditChange(
                                  p.product_id,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="bg-background border-white/10"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;